import { DropDownTreeChangeEvent, ItemProps } from '@progress/kendo-react-dropdowns';
import { GridCellProps } from '@progress/kendo-react-grid';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import GridCellBase, { useGridCellDisplayValue } from 'src/components/GridCellBase';
import TreeDropDown from 'src/components/design-system/DropDownTree';
import { InvoiceContext } from 'src/sections/invoice_details/InvoiceDetailsContext';
import { ClosedTag } from 'src/sections/view_checkbook/PeriodDropdown';
import { GlPeriodObject } from 'src/services/useGLPeriodApi';
import { hasPermission } from 'src/utils/JWT';
import { getBgColor } from 'src/utils/icons';
import { GLPeriodDropdownChildren } from 'src/utils/transformation';
import { processTreeData } from 'src/utils/treeDataOperations';
import { useContextSelector } from 'use-context-selector';
import { PanelEditFieldPermissions } from '..';

const selectField = 'selected';
const expandField = 'expanded';
const dataItemKey = 'id';
const textField = 'label';
const subItemsField = 'items';
const fields = { selectField, expandField, dataItemKey, subItemsField };

const InvoiceBudgetPeriodCell = (props: GridCellProps) => {
  const { field, dataItem, dataIndex, onChange } = props;

  const dropDownValues = useContextSelector(InvoiceContext, (value) => value.dropDownValues);
  const glPeriods = useContextSelector(InvoiceContext, (value) => value.glPeriods);
  const invoiceBudgetPeriods = useContextSelector(
    InvoiceContext,
    (value) => value.invoiceBudgetPeriods
  );
  const setSingleGlPeriodStartDate = useContextSelector(
    InvoiceContext,
    (value) => value.setSingleGlPeriodStartDate
  );
  const hasDistributionPermission = useContextSelector(
    InvoiceContext,
    (value) => value.hasDistributionPermission
  );

  const mpaPermissionNumber = useContextSelector(
    InvoiceContext,
    (value) => value.mpaPermissionNumber
  );
  const { value } = useGridCellDisplayValue(props);
  const [expanded, setExpanded] = useState([] as number[]);
  const [dropDownOpened, setDropDownOpened] = useState<boolean>(false);

  const findBudgetPeriod = glPeriods.find(
    (item) => item.glPeriodId === Number(value)
  ) as GlPeriodObject;

  const dropDownValue = useMemo(() => {
    const currentGlPeriods = dropDownValues.find(
      (value) => value.fiscalYear === findBudgetPeriod?.fiscalYear
    )?.items as GLPeriodDropdownChildren[];
    return currentGlPeriods
      ? currentGlPeriods.find((period) => period.glPeriodId === findBudgetPeriod?.glPeriodId)
      : null;
  }, [dropDownValues, findBudgetPeriod?.fiscalYear, findBudgetPeriod?.glPeriodId]);

  const initialExpandedPeriod = useCallback(() => {
    const currentDate = moment()
      .set({
        milliseconds: 0,
      })
      .toISOString()
      .replace('Z', '');
    const currentInvoiceBudgetPeriod =
      glPeriods.find((value) => currentDate > value.startDate && currentDate < value.endDate) ??
      ({} as GlPeriodObject);

    const initialExpand = dropDownValue
      ? [dropDownValue?.fiscalYear]
      : !isEmpty(dropDownValues)
      ? [currentInvoiceBudgetPeriod?.fiscalYear]
      : [];
    setExpanded(initialExpand);
  }, [dropDownValue, dropDownValues, glPeriods]);

  const otherGlPeriodsUsed = useCallback(() => {
    let glPeriodIds: number[] = [];
    invoiceBudgetPeriods.forEach((item) => {
      glPeriodIds.push(item.glPeriodId);
    });
    return glPeriodIds;
  }, [invoiceBudgetPeriods]);

  const handleChange = useCallback(
    (event: DropDownTreeChangeEvent) => {
      const { level = [], value = {} } = event;
      const { fiscalYear = 0, glPeriodId, isClosed } = value || {};

      if (otherGlPeriodsUsed().includes(glPeriodId) || isClosed === 1) {
        return;
      }
      if (isEmpty(level) || level.length > 1) {
        setDropDownOpened(false);
        onChange?.({
          dataIndex,
          dataItem,
          syntheticEvent: event.syntheticEvent,
          field,
          value: glPeriodId ?? null,
        });
      } else if (level.length === 1) {
        setExpanded((prev) => {
          if (prev.includes(fiscalYear)) {
            return prev.filter((item) => item !== fiscalYear);
          } else {
            return [...prev, fiscalYear];
          }
        });
      }
    },
    [dataIndex, dataItem, field, onChange, otherGlPeriodsUsed]
  );

  const Item = useCallback(
    (props: ItemProps) => {
      const { selected = false, label, content, parent = false, glPeriodId, isClosed } = props.item;
      const alreadyUsed = otherGlPeriodsUsed().includes(glPeriodId);

      return (
        <>
          {parent ? (
            <span onClick={() => setDropDownOpened(true)}>{content}</span>
          ) : alreadyUsed ? (
            <span
              style={{ color: `${selected ? '' : '#d0d0d0'}`, cursor: 'not-allowed' }}
              id="invoice-budget-period-popup-selected-item"
            >
              {label} {isClosed === 1 && <ClosedTag selected />}
            </span>
          ) : (
            <span
              className={`cursor-default hover:cursor-pointer ${
                isClosed === 1 ? 'text-[#d0d0d0] !cursor-not-allowed' : ''
              }`}
            >
              {label} {isClosed === 1 && <ClosedTag />}
            </span>
          )}
        </>
      );
    },
    [otherGlPeriodsUsed]
  );

  const treeData = useMemo(
    () => processTreeData(dropDownValues, { expanded, dropDownValue }, fields),
    [dropDownValue, dropDownValues, expanded]
  );

  useEffect(() => {
    if (invoiceBudgetPeriods.length === 1 && dropDownValue)
      setSingleGlPeriodStartDate(dropDownValue.startDate);
  }, [dropDownValue, invoiceBudgetPeriods.length, setSingleGlPeriodStartDate]);

  const error = useMemo(() => {
    const periodErrors = dataItem?.errors?.period || [];
    const periodStatusErrors = dataItem?.errors?.periodStatus || [];
    return [...periodErrors, ...periodStatusErrors];
  }, [dataItem?.errors?.period, dataItem?.errors?.periodStatus]);
  const isEditable = hasPermission(PanelEditFieldPermissions, mpaPermissionNumber);

  useEffect(() => {
    if (dropDownOpened) {
      initialExpandedPeriod();
      setTimeout(() => {
        const element = document.getElementById('invoice-budget-period-popup-selected-item');
        element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 200);
    }
  }, [dropDownOpened, initialExpandedPeriod]);
  return (
    <GridCellBase
      {...props}
      title={!isEmpty(dropDownValue) ? `${dropDownValue?.label}` : 'Select accounting period'}
      className={`${hasDistributionPermission ? getBgColor(error) : ''} !overflow-visible`}
    >
      {isEditable ? (
        <TreeDropDown
          key={dataItem.rowId}
          value={dropDownValue}
          placeholder="Select accounting period"
          data={treeData}
          onChange={handleChange}
          expanded={expanded}
          size={'small'}
          setExpanded={setExpanded}
          dropDownOpened={dropDownOpened}
          setDropDownOpened={setDropDownOpened}
          textField={textField}
          dataItemKey={dataItemKey}
          selectField={selectField}
          expandField={expandField}
          subItemsField={subItemsField}
          item={Item}
          valid={!error?.length}
          popupSettings={{
            popupClass: 'max-h-[45vh]',
          }}
          setInitialExpandValue={initialExpandedPeriod}
        />
      ) : (
        <p>{`${dropDownValue?.label}`}</p>
      )}
    </GridCellBase>
  );
};

export default InvoiceBudgetPeriodCell;
